<!-- Loading State -->
@if (loading) {
  <div class="flex justify-content-center align-items-center p-5">
    <p-progressSpinner aria-label="loading-commission-data" />
  </div>
}

<!-- Error State -->
@if (error && !loading) {
  <p-message severity="error" [text]="error" styleClass="w-full mb-3" />
}

<!-- Commission Content -->
@if (!loading && !error && statistics) {
  <div class="commission-content">
  <!-- Month Selector -->
  <div class="flex justify-content-between align-items-center mb-3">
    <h3 class="text-xl font-semibold text-900 m-0">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.MY_COMMISSION' | translate }}</h3>
    <p-select [options]="months" 
      [(ngModel)]="selectedMonth" 
      optionLabel="label"
      optionValue="value"
      (onChange)="onMonthChange()"
      [placeholder]="'STAFF.STAFF_PREFERENCE.COMMISSION.SELECT_MONTH' | translate"
      styleClass="w-full md:w-20rem"
      aria-label="select-commission-month" />
  </div>
  
  <!-- Statistics Header -->
  <div class="grid mb-3">
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-card shadow-2 p-3 border-round hover:shadow-4 transition-duration-300 cursor-pointer">
        <div class="flex align-items-center justify-content-between mb-3">
          <div>
            <span class="block text-500 font-medium mb-2 text-sm">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PERIOD_START' | translate }}</span>
            <div class="text-900 font-bold text-xl">{{ statistics.period_start_date | datetimeformat: 'dd MMM yyyy' }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-blue-100 border-round" style="width:3rem;height:3rem">
            <i class="fa fa-calendar-alt text-blue-500 text-xl"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-card shadow-2 p-3 border-round hover:shadow-4 transition-duration-300 cursor-pointer">
        <div class="flex align-items-center justify-content-between mb-3">
          <div>
            <span class="block text-500 font-medium mb-2 text-sm">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PERIOD_END' | translate }}</span>
            <div class="text-900 font-bold text-xl">{{ statistics.period_end_date | datetimeformat: 'dd MMM yyyy' }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-cyan-100 border-round" style="width:3rem;height:3rem">
            <i class="fa fa-calendar-check text-cyan-500 text-xl"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-card shadow-2 p-3 border-round hover:shadow-4 transition-duration-300 cursor-pointer">
        <div class="flex align-items-center justify-content-between mb-3">
          <div>
            <span class="block text-500 font-medium mb-2 text-sm">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TOTAL_COMMISSION' | translate }}</span>
            <div class="text-900 font-bold text-xl">{{ statistics.total_commission | currencyformat }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-green-100 border-round" style="width:3rem;height:3rem">
            <i class="fa fa-money-bill-wave text-green-500 text-xl"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 md:col-6 lg:col-3">
      <div class="surface-card shadow-2 p-3 border-round hover:shadow-4 transition-duration-300 cursor-pointer">
        <div class="flex align-items-center justify-content-between mb-3">
          <div>
            <span class="block text-500 font-medium mb-2 text-sm">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TOTAL_TRANSACTIONS' | translate }}</span>
            <div class="text-900 font-bold text-xl">{{ statistics.total_transactions }}</div>
          </div>
          <div class="flex align-items-center justify-content-center bg-orange-100 border-round" style="width:3rem;height:3rem">
            <i class="fa fa-list-alt text-orange-500 text-xl"></i>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Transactions Table -->
  <p-card>
    <p-table
      [value]="transactions"
      [expandedRowKeys]="expandedRows"
      dataKey="id"
      (onRowExpand)="onRowExpand($event)"
      (onRowCollapse)="onRowCollapse($event)"
      [paginator]="true"
      [rows]="10"
      [rowsPerPageOptions]="[10, 25, 50]"
      [responsive]="true"
      styleClass="p-datatable-sm"
      [tableStyle]="{ 'min-width': '60rem' }"
      aria-label="commission-transactions-table"
    >
      <ng-template pTemplate="caption">
        <div class="flex justify-content-end gap-2">
          <p-button 
            [label]="'STAFF.STAFF_PREFERENCE.COMMISSION.EXPAND_ALL' | translate" 
            icon="pi pi-plus" 
            [text]="true"
            size="small"
            (onClick)="expandAll()"
            aria-label="expand-all-rows" />
          <p-button 
            [label]="'STAFF.STAFF_PREFERENCE.COMMISSION.COLLAPSE_ALL' | translate" 
            icon="pi pi-minus" 
            [text]="true"
            size="small"
            (onClick)="collapseAll()"
            aria-label="collapse-all-rows" />
        </div>
      </ng-template>
      <ng-template #header>
        <tr>
          <th style="width: 3rem"></th>
          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.DATE' | translate }}</th>
          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PLAN_NAME' | translate }}</th>
          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.CLIENT_DETAILS' | translate }}</th>
          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TRANSACTION_TYPE' | translate }}</th>
          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.AMOUNT' | translate }}</th>
        </tr>
      </ng-template>
      <ng-template #body let-transaction let-expanded="expanded">
        <tr>
          <td>
            <p-button 
              type="button"
              [pRowToggler]="transaction" 
              [text]="true"
              [rounded]="true"
              [plain]="true"
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              [attr.aria-label]="'expand-row-' + transaction.id" />
          </td>
          <td>{{ transaction.date | date: 'dd MMM yyyy' }}</td>
          <td>{{ transaction.plan }}</td>
          <td>
            <div class="flex flex-column gap-1">
              <span class="font-semibold">{{ transaction.client.client_name }}</span>
              <span class="text-sm text-500">ID: {{ transaction.client.client_id }}</span>
            </div>
          </td>
          <td>
            <p-tag [icon]="getTransactionTypeIcon(transaction)" [value]="getTransactionType(transaction)" styleClass="text-sm" />
          </td>
          <td class="text-right font-semibold">{{ transaction.commission_amount_minor_curr | currencyformat }}</td>
        </tr>
      </ng-template>
      <ng-template #expandedrow let-transaction>
        <tr>
          <td colspan="6">
            <div class="p-3">
              <!-- Commission Details Section -->
              <div class="mb-4">
                <h5 class="text-900 font-semibold mb-2 pb-2 border-bottom-1 surface-border">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.COMMISSION_DETAILS' | translate }}</h5>
                <p-table [value]="[transaction]" styleClass="p-datatable-sm p-datatable-striped">
                  <ng-template #header>
                    <tr>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.RULE' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.SLAB' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TRANSACTION_TYPE_LABEL' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TRANSACTION_MODE' | translate }}</th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-item>
                    <tr>
                      <td>{{ item.rule }}</td>
                      <td>{{ item.slab }}</td>
                      <td>{{ item.commission_transaction_type }}</td>
                      <td>{{ getTransactionModeLabel(item.commission_transaction_mode) | translate }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>

              <!-- Transaction Details Section -->
              <div>
                <h5 class="text-900 font-semibold mb-2 pb-2 border-bottom-1 surface-border">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.TRANSACTION_DETAILS' | translate }}</h5>
                
                <!-- Appointment Details -->
                @if (transaction.appointment) {
                  <p-table [value]="[transaction.appointment]" styleClass="p-datatable-sm p-datatable-striped">
                  <ng-template #header>
                    <tr>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.APPOINTMENT_TYPE' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.COACH' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.START_TIME' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.END_TIME' | translate }}</th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-item>
                    <tr>
                      <td>{{ item.appointment_type }}</td>
                      <td>{{ item.coach_name }}</td>
                      <td>{{ item.start_time | datetimeformat: 'dd MMM yyyy HH:mm a' }}</td>
                      <td>{{ item.end_time | datetimeformat: 'dd MMM yyyy HH:mm a' }}</td>
                    </tr>
                  </ng-template>
                  </p-table>
                }

                <!-- Class Details -->
                @if (transaction.class) {
                  <p-table [value]="[transaction.class]" styleClass="p-datatable-sm p-datatable-striped">
                  <ng-template #header>
                    <tr>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.CLASS_NAME' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.CLASS_DATE' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.COACH' | translate }}</th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-item>
                    <tr>
                      <td>{{ item.class_name }}</td>
                      <td>{{ item.class_date | datetimeformat: 'dd MMM yyyy HH:mm a' }}</td>
                      <td>{{ item.coach_name }}</td>
                    </tr>
                  </ng-template>
                  </p-table>
                }

                <!-- Subscription Details -->
                @if (transaction.subscription) {
                  <p-table [value]="[transaction.subscription]" styleClass="p-datatable-sm p-datatable-striped">
                  <ng-template #header>
                    <tr>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.SUBSCRIPTION_NAME' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.SERVICE_CATEGORY' | translate }}</th>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PLAN_CATEGORY' | translate }}</th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-item>
                    <tr>
                      <td>{{ item.subscription_name }}</td>
                      <td>{{ item.service_category }}</td>
                      <td>{{ item.plan_category }}</td>
                    </tr>
                  </ng-template>
                  </p-table>
                }

                <!-- Invoice Details -->
                @if (transaction.invoice) {
                  <p-table [value]="[transaction.invoice]" styleClass="p-datatable-sm p-datatable-striped">
                    <ng-template #header>
                      <tr>
                        <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.INVOICE_NUMBER' | translate }}</th>
                        @if (transaction.invoice.retail_product) {
                          <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PRODUCT' | translate }}</th>
                        }
                      </tr>
                    </ng-template>
                    <ng-template #body let-item>
                      <tr>
                        <td>{{ item.invoice_prefix_number }}</td>
                        @if (item.retail_product) {
                          <td>{{ item.retail_product.product_name }} ({{ item.retail_product.sku_name }})</td>
                        }
                      </tr>
                    </ng-template>
                  </p-table>
                }

                <!-- Payment Details -->
                @if (transaction.payment) {
                  <p-table [value]="[transaction.payment]" styleClass="p-datatable-sm p-datatable-striped">
                  <ng-template #header>
                    <tr>
                      <th>{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.PAYMENT_ID' | translate }}</th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-item>
                    <tr>
                      <td>{{ item.payment_id }}</td>
                    </tr>
                  </ng-template>
                  </p-table>
                }
              </div>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template #emptymessage>
        <tr>
          <td colspan="6" class="text-center p-4">
            <div class="text-500">{{ 'STAFF.STAFF_PREFERENCE.COMMISSION.NO_TRANSACTIONS' | translate }}</div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>
  </div>
}

